import { useState, type ReactNode } from 'react';
import { ClipboardCopy, ExternalLink, LayoutTemplate, LogIn, Pencil, ScanSearch } from 'lucide-react';
import { platformLabel, signsInWith } from '@cp/shared';
import { Button, ButtonLink, Card, Fact, Facts, PlatformName } from '@/components/ui';
import { PlatformDialog } from '@/components/sites/PlatformDialog';
import { useCmsSignIn, useRevealCredential, useSiteCredentials } from '@/hooks/useCredentials';
import { useDetectPlatform } from '@/hooks/useSites';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import type { SiteDetail } from '@/types/api';

/**
 * What a site runs on and the way into its admin (ADR-0031): recognised from
 * its pages for a watched site, WordPress for one the panel runs, or said by
 * an operator — who can also correct either. How it was recognised is left
 * out: nobody working on the site needs it. With a CMS login kept, the panel
 * signs in with it at a click where it knows the form — WordPress's
 * (ADR-0035) or one described in the credential (ADR-0042) — and otherwise
 * opens the admin with the password on the clipboard.
 */
export function PlatformCard({
  site,
  mayEdit,
  mayUseCredentials,
}: {
  readonly site: SiteDetail;
  readonly mayEdit: boolean;
  /** Owner and administrators: those who may see the kept passwords. */
  readonly mayUseCredentials: boolean;
}): ReactNode {
  const [editing, setEditing] = useState(false);
  const info = site.platformInfo;
  const name = platformLabel(info?.name ?? null);
  const detect = useDetectPlatform(site.id, {
    onSuccess: () => toast.success('Определение платформы запущено'),
    onError: (error) => toast.error('Не удалось запросить проверку', errorText(error)),
  });
  const observed = site.engine === 'external';
  const platform = { engine: site.engine, platform: info?.name ?? null };
  const credentials = useSiteCredentials(site.id, mayUseCredentials);
  const kept = (credentials.data ?? []).find((credential) => signsInWith(platform, credential));
  // Any other CMS login with its password: the admin opened, the password copied.
  const byHand = kept ? undefined : (credentials.data ?? []).find((credential) => credential.kind === 'cms' && credential.hasPassword);
  const byHandAddress = byHand ? (byHand.url ?? site.adminUrl) : null;
  const signIn = useCmsSignIn(site.id, { onError: (error) => toast.error('Не удалось войти в админку', errorText(error)) });
  const reveal = useRevealCredential({ kind: 'site', id: site.id }, { onError: (error) => toast.error('Не удалось получить пароль', errorText(error)) });

  async function openWithPassword(credentialId: string, address: string): Promise<void> {
    const { password } = await reveal.mutateAsync(credentialId);
    try {
      await navigator.clipboard.writeText(password);
      toast.success('Пароль скопирован');
    } catch {
      toast.error('Буфер обмена недоступен', 'Пароль — на вкладке «Доступы»');
    }
    window.open(address, '_blank', 'noopener');
  }

  return (
    <Card
      title="Платформа и админка"
      icon={LayoutTemplate}
      actions={
        <div className="row-tight">
          {kept ? (
            <Button size="sm" variant="primary" icon={LogIn} busy={signIn.busy} title={`Как ${kept.login}`} onClick={() => signIn.signIn(kept.id)}>
              Войти в админку
            </Button>
          ) : null}
          {!kept && byHand && byHandAddress ? (
            <Button
              size="sm"
              variant="primary"
              icon={ClipboardCopy}
              busy={reveal.isPending}
              title={byHand.login ? `Логин: ${byHand.login}` : undefined}
              onClick={() => void openWithPassword(byHand.id, byHandAddress)}
            >
              Войти (пароль в буфере)
            </Button>
          ) : null}
          {site.adminUrl ? (
            <ButtonLink href={site.adminUrl} variant={kept || byHand ? 'ghost' : 'primary'} size="sm" icon={ExternalLink}>
              Открыть админку
            </ButtonLink>
          ) : null}
          {mayEdit ? (
            <Button size="sm" variant="ghost" icon={Pencil} onClick={() => setEditing(true)}>
              Изменить
            </Button>
          ) : null}
        </div>
      }
    >
      <Facts>
        <Fact label="Платформа">
          {name ? (
            <>
              <PlatformName platform={info?.name ?? null} label={name} />
              {info?.version ? <span className="muted"> · {info.version}</span> : null}
            </>
          ) : (
            // Only when nothing was recognised is asking again worth a button;
            // a recognised platform is checked again on its own.
            <span className="row-tight row-wrap">
              <span className="faint">{info?.checkedAt ? 'не определена' : 'не проверялась'}</span>
              {observed && mayEdit && info?.source !== 'operator' ? (
                <Button size="sm" variant="ghost" icon={ScanSearch} busy={detect.isPending} onClick={() => detect.mutate()}>
                  {info?.checkedAt ? 'Определить заново' : 'Определить'}
                </Button>
              ) : null}
            </span>
          )}
        </Fact>
        <Fact label="Админка">
          {site.adminUrl ? (
            <span>
              <a className="mono link-external" href={site.adminUrl} target="_blank" rel="noreferrer noopener">
                {site.adminUrl}
                <ExternalLink size={12} aria-hidden="true" />
              </a>
              {site.adminUrlByOperator ? <span className="muted"> · указана вручную</span> : null}
            </span>
          ) : (
            <span className="faint">не найдена</span>
          )}
        </Fact>
        {mayUseCredentials && credentials.isSuccess ? (
          <Fact label="Вход из панели">
            {kept ? (
              <span>
                одной кнопкой, как <span className="mono">{kept.login}</span>
              </span>
            ) : byHand ? (
              <span className="faint">
                страница входа, пароль в буфере обмена
              </span>
            ) : (
              <span className="faint">нет доступа вида «CMS»</span>
            )}
          </Fact>
        ) : null}
      </Facts>

      {editing ? <PlatformDialog site={site} onClose={() => setEditing(false)} /> : null}
    </Card>
  );
}
